import React, { useState, useEffect } from 'react';
import apiClient from '../../services/apiClient';
import {
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Space,
  message,
  Spin,
  Row,
  Col,
  Statistic,
  Progress
} from 'antd';
import {
  RocketOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  ScanOutlined
} from '@ant-design/icons';
import { ThemeContext } from '../../context/ThemeContext';
import { useContext } from 'react';
import './AdminPages.css';

const { Title, Paragraph } = Typography;

const SCANData = () => {
  const { isDark } = useContext(ThemeContext);
  const [loadingStates, setLoadingStates] = useState({});
  const [data, setData] = useState([]);

  // Bot configuration with dynamic API endpoints
  const botConfig = {
    'financial-strength-analysis': {
      name: 'Financial Strength Analysis',
      endpoint: '/trigger/data-sources/financial-strength-analysis',
      status: 'Active',
      successRate: 96,
      lastRun: '1 hour ago',
      totalRuns: 300,
      successfulRuns: 288
    },
    'market-activity-analysis': {
      name: 'Market Activity Analysis',
      endpoint: '/trigger/data-sources/market-activity-analysis',
      status: 'Active',
      successRate: 94,
      lastRun: '2 hours ago',
      totalRuns: 250,
      successfulRuns: 235
    },
    'complexity-analysis': {
      name: 'Complexity Analysis',
      endpoint: '/trigger/data-sources/complexity-analysis',
      status: 'Paused',
      successRate: 88,
      lastRun: '3 hours ago',
      totalRuns: 200,
      successfulRuns: 176
    },
    'governance-analysis': {
      name: 'Governance Analysis',
      endpoint: '/trigger/data-sources/governance-analysis',
      status: 'Failed',
      successRate: 0,
      lastRun: '4 hours ago',
      totalRuns: 10,
      successfulRuns: 0
    },
    'credit-rating-analysis': {
      name: 'Credit Rating Analysis',
      endpoint: '/trigger/data-sources/credit-rating-analysis',
      status: 'Active',
      successRate: 98,
      lastRun: '45 minutes ago',
      totalRuns: 400,
      successfulRuns: 392
    },
    'risk-assessment': {
      name: 'Risk Assessment',
      endpoint: '/trigger/data-sources/risk-assessment',
      status: 'Active',
      successRate: 97,
      lastRun: '30 minutes ago',
      totalRuns: 350,
      successfulRuns: 340
    }
  };

  // Convert bot config to array format for table
  const mockData = Object.entries(botConfig).map(([key, config]) => ({
    key,
    ...config
  }));

  useEffect(() => {
    setData(mockData);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Failed':
        return 'error';
      case 'Paused':
        return 'warning';
      case 'Disabled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active':
        return <CheckCircleOutlined />;
      case 'Failed':
        return <CloseCircleOutlined />;
      case 'Paused':
        return <PauseCircleOutlined />;
      case 'Disabled':
        return <MinusCircleOutlined />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  const handleRunAll = async () => {
    // Set loading state for all buttons
    setLoadingStates(prev => {
      const newStates = {};
      mockData.forEach(item => {
        newStates[item.key] = true;
      });
      return newStates;
    });
    
    message.loading('Running all SCAN data bots...', 0);
    
    try {
      // Execute all bots in parallel
      const promises = mockData.map(item => {
        const config = botConfig[item.key];
        if (config) {
          return executeBot(item.key, config);
        }
        return Promise.resolve();
      });
      
      await Promise.allSettled(promises);
      
      message.destroy();
      message.success('All SCAN data bots execution completed!');
    } catch (error) {
      message.destroy();
      message.error('Some bots failed to execute. Check individual bot status.');
    } finally {
      // Clear loading state for all buttons
      setLoadingStates({});
    }
  };

  // Dynamic bot execution function
  const executeBot = async (botKey, config) => {
    const baseUrl = process.env.REACT_APP_BOT_URL;
    const fullUrl = `${baseUrl}${config.endpoint}`;
    
    try {
      // Execute GET request
      const response = await apiClient.get(fullUrl);
      
      console.log('API Response:', response.data);
      
      // Handle the API response structure
      if (response.data.status === 'success') {
        message.destroy(); // Remove loading message
        message.success(response.data.message || `${config.name} executed successfully!`);
        console.log('Bot execution result:', response.data.result);
        
        // Update bot status to success
        updateBotStatus(botKey, 'success');
      } else {
        message.destroy(); // Remove loading message
        message.error(response.data.message || `Error running ${config.name}`);
        
        // Update bot status to failed
        updateBotStatus(botKey, 'failed');
      }
      
    } catch (error) {
      console.error('API Error:', error);
      message.destroy(); // Remove loading message
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || error.response.statusText;
        message.error(`Error running ${config.name}: ${errorMessage}`);
      } else if (error.request) {
        // Request was made but no response received
        message.error(`Error running ${config.name}: No response from server`);
      } else {
        // Something else happened
        message.error(`Error running ${config.name}: ${error.message}`);
      }
      
      // Update bot status to failed
      updateBotStatus(botKey, 'failed');
    }
  };

  // Update bot status after execution
  const updateBotStatus = (botKey, status) => {
    setData(prevData => 
      prevData.map(item => 
        item.key === botKey 
          ? { 
              ...item, 
              status: status === 'success' ? 'Active' : 'Failed',
              lastRun: 'Just now',
              totalRuns: item.totalRuns + 1,
              successfulRuns: status === 'success' ? item.successfulRuns + 1 : item.successfulRuns,
              successRate: Math.round(((status === 'success' ? item.successfulRuns + 1 : item.successfulRuns) / (item.totalRuns + 1)) * 100)
            }
          : item
      )
    );
  };

  const handleRunBot = (record) => {
    // Set loading state for this specific button
    setLoadingStates(prev => ({ ...prev, [record.key]: true }));
    
    // Get bot configuration
    const config = botConfig[record.key];
    
    if (!config) {
      message.error(`No configuration found for bot: ${record.name}`);
      setLoadingStates(prev => ({ ...prev, [record.key]: false }));
      return;
    }
    
    // Execute bot dynamically
    executeBot(record.key, config)
      .finally(() => {
        // Clear loading state for this specific button
        setLoadingStates(prev => ({ ...prev, [record.key]: false }));
      });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Success Rate',
      dataIndex: 'successRate',
      key: 'successRate',
      render: (rate) => (
        rate !== null ? (
          <Space>
            <Progress 
              percent={rate} 
              size="small" 
              strokeColor={rate >= 90 ? '#52c41a' : rate >= 70 ? '#faad14' : '#ff4d4f'}
              showInfo={false}
            />
            <span>{rate}%</span>
          </Space>
        ) : (
          <span>-</span>
        )
      ),
    },
    {
      title: 'Last Run',
      dataIndex: 'lastRun',
      key: 'lastRun',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleRunBot(record)}
            loading={loadingStates[record.key] || false}
            style={{
              width: 101,
              height: 37,
              borderRadius: 50,
              backgroundColor: '#68D241E8',
              border: 'none',
              opacity: 1
            }}
            className="custom-run-button"
          >
            Run
          </Button>
        </Space>
      ),
    },
  ];

  const totalBots = data.length;
  const activeBots = data.filter(item => item.status === 'Active').length;
  const totalRuns = data.reduce((sum, item) => sum + item.totalRuns, 0);
  const totalSuccessfulRuns = data.reduce((sum, item) => sum + item.successfulRuns, 0);
  const overallSuccessRate = totalRuns > 0 ? Math.round((totalSuccessfulRuns / totalRuns) * 100) : 0;

  return (
    <div className={`admin-page ${isDark ? 'dark' : ''}`}>
      {/* Hero Section */}
      <div className="admin-header-banner">
        <div className="hero-content">
          <Title level={1} className="hero-title-news">
            SCAN Data
          </Title>
          <Paragraph className="hero-description">
            Monitor SCAN (Securities and Commodities Authority Network) data and regulatory compliance.
          </Paragraph>
        </div>
      </div>

      {/* Main Content */}
      <Card className="admin-main-card">
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              SCAN Data
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              onClick={handleRunAll}
              loading={Object.values(loadingStates).some(loading => loading)}
            >
              Run All Bots
            </Button>
          </Col>
        </Row>

     

        {/* Data Table */}
        <Table
          columns={columns}
          dataSource={data}
          pagination={false}
          loading={Object.values(loadingStates).some(loading => loading)}
          className="admin-table"
        />
      </Card>
    </div>
  );
};

export default SCANData;
